import * as H from "history";
import * as React from "react";
import { ToastContainer } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import { ModalProvider } from "styled-react-modal";
import styled, { ThemeProvider } from "styled-components";

import { GlobalStyles } from "src/library/components/GlobalStyles";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { SettingsError } from "src/library/components/SettingsError";
import Grid from "src/library/components/Hedron";
import { theme as DefaultTheme } from "src/library/utils/theme";
import type { IStore } from "src/core/shared/store";
import { addLocation } from "src/core/shared/store/global/global.actions";
import useGlobalSettings from "src/core/shared/hooks/useGlobalSettings";
import { MenuProvider } from "src/core/shared/hooks/menu/useMenu";
import { AppRouter } from "src/pages/Router";

import NotificationBannerProvider from "../context/NotificationBanner/NotificationBannerContext";

interface IProps {
  history: H.History<any>;
}

export const AppSettingsProvider: React.ReactFCWithChildren<IProps> = ({ history }) => {
  const dispatch = useDispatch();
  const { historyStore } = useMappedState((store: IStore) => ({ historyStore: store.global.history }));

  const [
    {
      isAWSConfigured, globalSettings: settings, error
    },
    { getGlobalSettings }
  ] = useGlobalSettings();

  const theme = React.useMemo(() => {
    if (settings && settings.theme) {
      return settings.theme;
    }

    return DefaultTheme;
  }, [ settings ]);

  // On mount
  React.useEffect(() => {
    const unlisten = history.listen(location => {
      if (!historyStore || !historyStore[ 0 ] || historyStore[ 0 ].pathname !== location.pathname) {
        dispatch(addLocation(location));
      }
    });

    // If there are no settings in the store, attempt to retrieve them from
    // the DB.

    getGlobalSettings(null);
    dispatch(addLocation(history.location));

    return () => {
      unlisten();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show the loading spinner if there are no settings
  if (!error && settings === null) {
    return <LoadingSpinner type="circular" />;
  }

  if (error) {
    return <SettingsError />;
  }

  if (!(settings && isAWSConfigured)) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <Grid.Provider
        breakpoints={settings.theme.breakpoints}
        padding="0"
      >
        <RootContainer id="root-container">
          <ModalProvider>
            <>
              <GlobalStyles />

              <ToastContainer />

              <NotificationBannerProvider>
                <MenuProvider
                  history={history}
                  terminology={settings.terminology}
                >
                  <AppRouter
                    history={history}
                    terminology={settings.terminology}
                  />
                </MenuProvider>
              </NotificationBannerProvider>
            </>
          </ModalProvider>
        </RootContainer>
      </Grid.Provider>
    </ThemeProvider>
  );
};

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100vw;
  height: 100vh;
`;
